/**
 * "What to cook with what I have" ([smakota_pantry] page).
 *
 * The visitor picks products (combobox with suggestions, popular chips, a
 * "kitchen basics" shortcut); matching recipes come from smakota/v1/pantry
 * (ranked: nothing missing first) and their cards from winonweb/v1/cards.
 * Picked products are remembered in localStorage.
 */

const KEY = 'smakota-pantry';
const BASICS = [
	'onion',
	'garlic',
	'carrot',
	'potato',
	'egg',
	'flour',
	'milk',
	'butter',
	'sour-cream',
	'cheese',
	'rice',
	'buckwheat',
	'pasta',
	'bread',
];

const load = () => {
	try {
		const v = JSON.parse( window.localStorage.getItem( KEY ) || '[]' );
		return Array.isArray( v ) ? v : [];
	} catch ( e ) {
		return [];
	}
};
const store = ( list ) => {
	try {
		window.localStorage.setItem( KEY, JSON.stringify( list ) );
	} catch ( e ) {
		// Not remembered — still works for this visit.
	}
};

export default function initPantry() {
	const root = document.querySelector( '[data-pantry]' );
	if ( ! root ) {
		return;
	}
	const cfg = window.winonweb || {};
	const dict = JSON.parse( root.dataset.dict || '[]' );
	const t = JSON.parse( root.dataset.i18n || '{}' );
	const byKey = new Map( dict.map( ( d ) => [ d.k, d ] ) );

	const input = root.querySelector( '#pantry-input' );
	const list = root.querySelector( '#pantry-options' );
	const live = root.querySelector( '[data-pantry-live]' );
	const chips = root.querySelector( '[data-pantry-chips]' );
	const quick = root.querySelector( '[data-pantry-quick]' );
	const clear = root.querySelector( '[data-pantry-clear]' );
	const status = root.querySelector( '[data-pantry-status]' );
	const results = root.querySelector( '[data-pantry-results]' );

	let have = load().filter( ( k ) => byKey.has( k ) );
	let options = [];
	let active = -1;
	let request = 0;
	// Diet filters are per visit, not remembered: "show me only vegan" is a
	// mood, and a filter still on from last week would silently hide recipes.
	const diets = [];

	const say = ( el, text ) => {
		el.textContent = '';
		window.setTimeout( () => {
			el.textContent = text;
		}, 40 );
	};

	/* ---------------- Picked products ---------------- */

	const renderChips = () => {
		chips.replaceChildren(
			...have.map( ( k ) => {
				const li = document.createElement( 'li' );
				const b = document.createElement( 'button' );
				b.type = 'button';
				b.className = 'pantry__chip';
				b.setAttribute(
					'aria-label',
					( t.remove || '{x}' ).replace( '{x}', byKey.get( k ).l )
				);
				b.innerHTML = '<span></span><span aria-hidden="true">×</span>';
				b.firstChild.textContent = byKey.get( k ).l;
				b.addEventListener( 'click', () => {
					toggle( k, false );
					input.focus();
				} );
				li.append( b );
				return li;
			} )
		);

		// Popular products not yet picked. Only `main` ones: those are what decide
		// whether a recipe matches, so a shortcut to vanilla would waste a slot.
		// Minor products stay findable by typing.
		quick.replaceChildren(
			...dict
				.filter( ( d ) => ! have.includes( d.k ) && d.g !== 'minor' )
				.slice( 0, 14 )
				.map( ( d ) => {
					const li = document.createElement( 'li' );
					const b = document.createElement( 'button' );
					b.type = 'button';
					b.className = 'chips__item pantry__quick-item';
					b.textContent = `+ ${ d.l }`;
					b.addEventListener( 'click', () => toggle( d.k, true ) );
					li.append( b );
					return li;
				} )
		);
		clear.hidden = have.length === 0;
	};

	const toggle = ( key, on ) => {
		have = on
			? [ ...new Set( [ ...have, key ] ) ]
			: have.filter( ( k ) => k !== key );
		store( have );
		renderChips();
		markCatalog();
		search();
	};

	/* ---------------- Combobox ---------------- */

	const norm = ( s ) => s.toLowerCase().replace( /[’'ʼ]/g, '' ).trim();

	// The server's own pattern for each product (Pantry::key_for), compiled once.
	// Without it the picker only knew labels, so a word longer than its label found
	// nothing: "яйц" offered Яйця and "яйце" offered nothing at all — the opposite
	// of what typing more should do.
	const rx = new Map();
	const pattern = ( d ) => {
		if ( ! rx.has( d.k ) ) {
			let re = null;
			try {
				re = d.r
					? new RegExp( `(?:^|[^\\p{L}])(?:${ d.r })`, 'iu' )
					: null;
			} catch ( e ) {
				re = null; // A pattern PHP accepts but JS does not: fall back to prefixes.
			}
			rx.set( d.k, re );
		}
		return rx.get( d.k );
	};

	const matches = ( q ) => {
		const nq = norm( q );
		if ( ! nq ) {
			return [];
		}
		return dict
			.filter( ( d ) => ! have.includes( d.k ) )
			.filter( ( d ) => {
				// Prefix first, so the list narrows naturally as letters arrive.
				const words = `${ d.l } ${ d.s || '' }`.split( /[\s,/]+/ );
				if (
					words.some( ( w ) => norm( w ).startsWith( nq ) ) ||
					norm( d.l ).startsWith( nq )
				) {
					return true;
				}
				// Then the recipe pattern, which catches the fuller word.
				const re = pattern( d );
				return !! re && re.test( nq );
			} )
			.slice( 0, 8 );
	};

	const close = () => {
		list.hidden = true;
		input.setAttribute( 'aria-expanded', 'false' );
		input.removeAttribute( 'aria-activedescendant' );
		active = -1;
	};

	const setActive = ( i ) => {
		options.forEach( ( o, n ) =>
			o.setAttribute( 'aria-selected', String( n === i ) )
		);
		active = i;
		if ( options[ i ] ) {
			input.setAttribute( 'aria-activedescendant', options[ i ].id );
		}
	};

	const pick = ( key ) => {
		input.value = '';
		close();
		toggle( key, true );
	};

	const open = () => {
		const found = matches( input.value );
		list.replaceChildren();
		options = found.map( ( d, i ) => {
			const li = document.createElement( 'li' );
			li.id = `pantry-opt-${ i }`;
			li.className = 'search-suggest__option';
			li.setAttribute( 'role', 'option' );
			li.setAttribute( 'aria-selected', 'false' );
			li.textContent = d.l;
			li.addEventListener( 'mousedown', ( e ) => e.preventDefault() );
			li.addEventListener( 'click', () => pick( d.k ) );
			list.append( li );
			return li;
		} );
		if ( ! options.length ) {
			// Say so instead of closing in silence: a reader who types a product we
			// do not carry cannot otherwise tell the difference between "not in the
			// list" and "the field is broken".
			close();
			if ( norm( input.value ).length >= 2 ) {
				const li = document.createElement( 'li' );
				li.className = 'search-suggest__empty';
				li.textContent = ( t.noProduct || '' ).replace(
					'{x}',
					input.value.trim()
				);
				list.append( li );
				list.hidden = false;
				say( live, li.textContent );
			}
			return;
		}
		list.hidden = false;
		input.setAttribute( 'aria-expanded', 'true' );
		setActive( 0 );
		say(
			live,
			( t.options || '' ).replace( '{n}', String( options.length ) )
		);
	};

	input.disabled = false;
	input.addEventListener( 'input', open );
	input.addEventListener( 'keydown', ( e ) => {
		if ( e.key === 'ArrowDown' && options.length ) {
			e.preventDefault();
			if ( list.hidden ) {
				open();
			} else {
				setActive( ( active + 1 ) % options.length );
			}
		} else if ( e.key === 'ArrowUp' && ! list.hidden ) {
			e.preventDefault();
			setActive( active <= 0 ? options.length - 1 : active - 1 );
		} else if ( e.key === 'Enter' ) {
			e.preventDefault();
			const found = matches( input.value );
			if ( ! list.hidden && active >= 0 && found[ active ] ) {
				pick( found[ active ].k );
			} else if ( found.length ) {
				pick( found[ 0 ].k );
			}
		} else if ( e.key === 'Escape' ) {
			close();
		}
	} );
	input.addEventListener( 'blur', () => window.setTimeout( close, 120 ) );

	root.querySelector( '[data-pantry-basics]' ).addEventListener(
		'click',
		() => {
			have = [
				...new Set( [
					...have,
					...BASICS.filter( ( k ) => byKey.has( k ) ),
				] ),
			];
			store( have );
			renderChips();
			search();
		}
	);
	clear.addEventListener( 'click', () => {
		have = [];
		store( have );
		renderChips();
		search();
		input.focus();
	} );

	/* ---------------- Results ---------------- */

	const badge = ( item ) => {
		const box = document.createElement( 'p' );
		box.className = `pantry-badge ${
			item.missing.length
				? 'pantry-badge--missing'
				: 'pantry-badge--ready'
		}`;
		box.textContent = item.missing.length
			? ( t.missing || '{x}' ).replace( '{x}', item.missing.join( ', ' ) )
			: t.haveAll;
		if ( item.extra && item.extra.length ) {
			const extra = document.createElement( 'span' );
			extra.className = 'pantry-badge__extra';
			extra.textContent = ( t.extra || '{x}' ).replace(
				'{x}',
				item.extra.join( ', ' )
			);
			box.append( extra );
		}
		return box;
	};

	const section = ( title, items, cards ) => {
		const wrap = document.createElement( 'section' );
		wrap.className = 'pantry__group';
		const h = document.createElement( 'h2' );
		h.className = 'pantry__group-title';
		h.textContent = `${ title } · ${ items.length }`;
		const ul = document.createElement( 'ul' );
		ul.className = 'recipe-grid pantry__grid';
		ul.setAttribute( 'role', 'list' );
		items.forEach( ( item ) => {
			const html = cards.get( item.id );
			if ( ! html ) {
				return;
			}
			const tpl = document.createElement( 'template' );
			tpl.innerHTML = html; // Same-origin card markup.
			const li = tpl.content.firstElementChild;
			li.append( badge( item ) );
			ul.append( li );
		} );
		wrap.append( h, ul );
		return wrap;
	};

	async function search() {
		const mine = ++request;
		if ( ! have.length ) {
			results.replaceChildren();
			status.textContent = t.start;
			return;
		}
		status.textContent = t.loading;
		try {
			const res = await fetch(
				`${ cfg.restCore }pantry?have=${ have.join(
					','
				) }&lang=${ encodeURIComponent( cfg.lang || '' ) }${
					diets.length
						? `&diet=${ encodeURIComponent( diets.join( ',' ) ) }`
						: ''
				}`
			);
			const data = await res.json();
			const items = Array.isArray( data.items ) ? data.items : [];
			if ( mine !== request ) {
				return;
			}
			if ( ! items.length ) {
				results.replaceChildren();
				say( status, t.none );
				return;
			}
			const cardsRes = await fetch(
				`${ cfg.rest }cards?ids=${ items
					.map( ( i ) => i.id )
					.join( ',' ) }&lang=${ encodeURIComponent(
					cfg.lang || ''
				) }`
			);
			const cardData = await cardsRes.json();
			if ( mine !== request ) {
				return;
			}
			const cards = new Map(
				( cardData.items || [] ).map( ( c ) => [
					Number( c.id ),
					c.html,
				] )
			);
			const ready = items.filter( ( i ) => ! i.missing.length );
			const almost = items.filter( ( i ) => i.missing.length );
			results.replaceChildren(
				...( ready.length
					? [ section( t.readyTitle, ready, cards ) ]
					: [] ),
				...( almost.length
					? [ section( t.almostTitle, almost, cards ) ]
					: [] )
			);
			say(
				status,
				( t.found || '' ).replace( '{n}', String( items.length ) )
			);
			restoreScroll();
		} catch ( e ) {
			if ( mine === request ) {
				say( status, t.failed );
			}
		}
	}

	/* ---------------- Coming back from a recipe ---------------- */

	// The results arrive over fetch, so by the time the browser restores the
	// scroll position on a back navigation the page is still empty and the
	// position is lost. Remember it with the selection it belonged to and put it
	// back once the same results have rendered.
	const SCROLL_KEY = 'smakota-pantry-scroll';
	let restored = false;

	const restoreScroll = () => {
		if ( restored ) {
			return;
		}
		restored = true;
		try {
			const saved = JSON.parse(
				window.sessionStorage.getItem( SCROLL_KEY ) || 'null'
			);
			window.sessionStorage.removeItem( SCROLL_KEY );
			if ( saved && saved.have === have.join( ',' ) && saved.y > 0 ) {
				window.scrollTo( { top: saved.y, behavior: 'instant' } );
			}
		} catch ( e ) {
			// Storage blocked: the page simply opens at the top.
		}
	};

	window.addEventListener( 'pagehide', () => {
		try {
			window.sessionStorage.setItem(
				SCROLL_KEY,
				JSON.stringify( {
					have: have.join( ',' ),
					y: Math.round( window.scrollY ),
				} )
			);
		} catch ( e ) {
			// Not remembered — the list still works.
		}
	} );

	/* ---------------- Catalogue below the tool ---------------- */

	// Server-rendered so a crawler and a reader without JS still see the whole
	// list. With JS each row becomes a button: seeing "Гранат 4" and having to
	// scroll back up and retype it was the long way round.
	// One toast, reused: a reader adding five things in a row gets five messages
	// in the same place, not a stack of them.
	let toastTimer = 0;
	const toast = ( text ) => {
		let box = root.querySelector( '.pantry__toast' );
		if ( ! box ) {
			box = document.createElement( 'p' );
			box.className = 'pantry__toast';
			box.setAttribute( 'role', 'status' );
			root.append( box );
		}
		box.textContent = text;
		box.classList.add( 'is-on' );
		window.clearTimeout( toastTimer );
		toastTimer = window.setTimeout(
			() => box.classList.remove( 'is-on' ),
			2400
		);
	};

	const catalogItems = new Map();
	root.querySelectorAll( '.pantry__catalog-item[data-key]' ).forEach(
		( li ) => {
			const key = li.dataset.key;
			if ( ! byKey.has( key ) ) {
				return;
			}
			const b = document.createElement( 'button' );
			b.type = 'button';
			b.className = 'pantry__catalog-button';
			b.append( ...li.childNodes );
			li.append( b );
			b.addEventListener( 'click', () => {
				const label = byKey.get( key ).l;
				const on = have.includes( key );
				toggle( key, ! on );
				// Reading the catalogue is the whole point of being down here, so
				// nothing jumps back to the field: the toast reports the change
				// where the finger is, and `role="status"` reads it out loud.
				toast(
					( ( on ? t.removed : t.added ) || '{x}' ).replace(
						'{x}',
						label
					)
				);
			} );
			catalogItems.set( key, b );
		}
	);

	const markCatalog = () => {
		catalogItems.forEach( ( b, key ) => {
			const on = have.includes( key );
			b.setAttribute( 'aria-pressed', String( on ) );
			b.setAttribute(
				'aria-label',
				( on ? t.remove || '{x}' : t.addProduct || '{x}' ).replace(
					'{x}',
					byKey.get( key ).l
				)
			);
		} );
	};

	/* ---------------- Diet filters ---------------- */

	root.querySelectorAll( '[data-pantry-diet]' ).forEach( ( button ) => {
		button.addEventListener( 'click', () => {
			const key = button.dataset.pantryDiet;
			const at = diets.indexOf( key );
			if ( at >= 0 ) {
				diets.splice( at, 1 );
			} else {
				diets.push( key );
			}
			button.setAttribute( 'aria-pressed', String( at < 0 ) );
			search();
		} );
	} );

	renderChips();
	markCatalog();
	search();
}
